import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';

function Vote() {
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState({ three: null, two: null, one: null });
  const [opponent, setOpponent] = useState('');
  const [matchResult, setMatchResult] = useState('');
  const [teamScore, setTeamScore] = useState('');
  const [opponentScore, setOpponentScore] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const editVote = location.state?.editVote;

  useEffect(() => {
    loadPlayers();
    
    if (editVote) {
      setOpponent(editVote.opponent || '');
      
      if (editVote.result) {
        const resultStr = editVote.result;
        if (resultStr.toLowerCase().includes('win')) {
          setMatchResult('Win');
        } else if (resultStr.toLowerCase().includes('loss')) {
          setMatchResult('Loss');
        } else if (resultStr.toLowerCase().includes('draw')) {
          setMatchResult('Draw');
        }
        
        const scoreMatch = resultStr.match(/(\d+)-(\d+)/);
        if (scoreMatch) {
          setTeamScore(scoreMatch[1]);
          setOpponentScore(scoreMatch[2]);
        }
      }
      
      editVote.votes.forEach(v => {
        const player = players.find(p => p.id === v.playerId);
        if (player) {
          if (v.points === 3) setSelected(prev => ({ ...prev, three: player }));
          if (v.points === 2) setSelected(prev => ({ ...prev, two: player }));
          if (v.points === 1) setSelected(prev => ({ ...prev, one: player }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editVote]);

  const loadPlayers = async () => {
    try {
      const teamDoc = await getDoc(doc(db, 'teams', auth.currentUser.uid));
      if (teamDoc.exists()) {
        const playersList = teamDoc.data().players || [];
        setPlayers(playersList);
        
        if (editVote) {
          editVote.votes.forEach(v => {
            const player = playersList.find(p => p.id === v.playerId);
            if (player) {
              if (v.points === 3) setSelected(prev => ({ ...prev, three: player }));
              if (v.points === 2) setSelected(prev => ({ ...prev, two: player }));
              if (v.points === 1) setSelected(prev => ({ ...prev, one: player }));
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const selectPlayer = (points, player) => {
    const alreadySelected = Object.entries(selected).find(
      ([key, value]) => value?.id === player.id && key !== points
    );

    if (alreadySelected) {
      alert('Player already selected');
      return;
    }

    setSelected(prev => ({ ...prev, [points]: player }));
  };

  const submitVote = async () => {
    if (!selected.three || !selected.two || !selected.one) {
      alert('Please select all 3 players');
      return;
    }

    setLoading(true);

    try {
      const teamDoc = await getDoc(doc(db, 'teams', auth.currentUser.uid));
      const currentVotes = teamDoc.data().votes || [];
      
      let resultString = '';
      if (matchResult && teamScore && opponentScore) {
        resultString = `${matchResult} ${teamScore}-${opponentScore}`;
      }

      if (editVote) {
        const updatedVotes = currentVotes.map(v => {
          if (v.id === editVote.id) {
            return {
              ...v,
              opponent: opponent.trim() || 'Unknown',
              result: resultString,
              votes: [
                { playerId: selected.three.id, playerName: selected.three.name, points: 3 },
                { playerId: selected.two.id, playerName: selected.two.name, points: 2 },
                { playerId: selected.one.id, playerName: selected.one.name, points: 1 },
              ]
            };
          }
          return v;
        });

        await updateDoc(doc(db, 'teams', auth.currentUser.uid), {
          votes: updatedVotes
        });

        alert('Round updated successfully!');
        navigate('/history');
      } else {
        const roundNumber = currentVotes.length + 1;

        const newVote = {
          id: Date.now().toString(),
          round: roundNumber,
          date: new Date().toISOString(),
          opponent: opponent.trim() || 'Unknown',
          result: resultString,
          votes: [
            { playerId: selected.three.id, playerName: selected.three.name, points: 3 },
            { playerId: selected.two.id, playerName: selected.two.name, points: 2 },
            { playerId: selected.one.id, playerName: selected.one.name, points: 1 },
          ]
        };

        await updateDoc(doc(db, 'teams', auth.currentUser.uid), {
          votes: arrayUnion(newVote)
        });

        alert(`Round ${roundNumber} voting complete!`);
        navigate('/leaderboard');
      }
    } catch (error) {
      alert('Failed to submit vote');
    } finally {
      setLoading(false);
    }
  };

  const renderVoteSection = (points, label, color) => {
    const player = selected[points];
    const availablePlayers = players.filter(p => {
      const sel = Object.entries(selected).find(
        ([key, value]) => value?.id === p.id && key !== points
      );
      return !sel;
    });

    return (
      <div className="card">
        <h3 style={{ color, marginBottom: '12px', fontSize: '18px' }}>
          {label} ({points === 'three' ? 3 : points === 'two' ? 2 : 1} points)
        </h3>
        
        {player ? (
          <div style={{ 
            padding: '16px',
            background: '#f5f5f5',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontWeight: '600', color: '#243665' }}>
                {player.name}
              </div>
              {player.number && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  #{player.number}
                </div>
              )}
            </div>
            <button
              onClick={() => setSelected(prev => ({ ...prev, [points]: null }))}
              style={{
                background: 'none',
                border: 'none',
                color: '#d32f2f',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <div style={{ 
            display: 'flex',
            gap: '12px',
            overflowX: 'auto',
            padding: '4px 0'
          }}>
            {availablePlayers.map(p => (
              <div
                key={p.id}
                onClick={() => selectPlayer(points, p)}
                style={{
                  minWidth: '100px',
                  padding: '16px',
                  background: 'white',
                  border: '2px solid #243665',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '14px' }}>
                  {p.name}
                </div>
                {p.number && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    #{p.number}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container">
      <div className="header">
        <h1>{editVote ? 'Edit Round' : 'Vote 3-2-1'}</h1>
        <button onClick={() => navigate('/')}>Cancel</button>
      </div>

      <div style={{ padding: '20px 0' }}>
        {editVote && (
          <div style={{ 
            background: '#fff3cd', 
            padding: '12px', 
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            Editing Round {editVote.round}
          </div>
        )}
        
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Select your top 3 players for this round
        </p>

        {renderVoteSection('three', '🥇 Best Player', '#FFD700')}
        {renderVoteSection('two', '🥈 Second Best', '#C0C0C0')}
        {renderVoteSection('one', '🥉 Third Best', '#CD7F32')}

        <div className="card">
          <h3 style={{ marginBottom: '16px', color: '#243665' }}>
            Match Details (Optional)
          </h3>
          
          <div>
            <label className="input-label">OPPONENT</label>
            <input
              type="text"
              className="input"
              value={opponent}
              onChange={(e) => setOpponent(e.target.value)}
              placeholder="e.g., Eagles SC"
            />
          </div>

          <div>
            <label className="input-label">RESULT</label>
            <select
              className="input"
              value={matchResult}
              onChange={(e) => setMatchResult(e.target.value)}
              style={{ cursor: 'pointer' }}
            >
              <option value="">Select result...</option>
              <option value="Win">Win</option>
              <option value="Loss">Loss</option>
              <option value="Draw">Draw</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label className="input-label">YOUR SCORE</label>
              <input
                type="number"
                className="input"
                value={teamScore}
                onChange={(e) => setTeamScore(e.target.value)}
                placeholder="0"
                min="0"
                inputMode="numeric"
              />
            </div>

            <div>
              <label className="input-label">OPPONENT SCORE</label>
              <input
                type="number"
                className="input"
                value={opponentScore}
                onChange={(e) => setOpponentScore(e.target.value)}
                placeholder="0"
                min="0"
                inputMode="numeric"
              />
            </div>
          </div>
        </div>

        <button
          className="button"
          onClick={submitVote}
          disabled={loading || !selected.three || !selected.two || !selected.one}
        >
          {loading ? (editVote ? 'Updating...' : 'Submitting...') : (editVote ? '✓ Update Round' : '✓ Submit Votes')}
        </button>
      </div>
    </div>
  );
}

export default Vote;
